/**
 * /arena/join — Gym Registration (3 steps).
 * After submit shows pending review confirmation.
 */
import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, Camera, Image as ImageIcon, Check, X, Mail, Lock, Eye, EyeOff, User as UserIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { compressImage } from "../../utils/imageCompress";
import { toast } from "sonner";
import { ArenaShield } from "../../components/arena/ArenaAtoms";

const GYM_TYPES = [
  { v: "weights", l: "Weights" }, { v: "crossfit", l: "CrossFit" },
  { v: "yoga", l: "Yoga Studio" }, { v: "mixed", l: "Mixed" }, { v: "boutique", l: "Boutique" },
];
const AMENITIES = ["AC", "Parking", "Showers", "Lockers", "Cardio", "Free Weights", "Yoga Studio", "Group Classes", "Protein Bar", "WiFi"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ArenaJoin() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const register = useAuthStore((s) => s.register);
  const loc = useLocation();
  const refToken = new URLSearchParams(loc.search).get("ref");

  // Step 0 = "Create owner account" (only for guests). Signed-in users start at 1.
  const initialStep = user ? 1 : 0;
  const [step, setStep] = useState(initialStep);
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    // Step 0 — owner account (empty unless already signed in)
    account_name: user?.name || "",
    account_email: user?.email || "",
    account_password: "",
    account_password_confirm: "",
    account_phone: "",
    consent_age: false,
    consent_terms: false,
    // Existing gym fields
    gym_name: "", gym_type: "weights",
    city: "", state: "", pincode: "", address: "",
    owner_name: user?.name || "", owner_phone: "", owner_email: user?.email || "",
    member_count_approx: 30, trainer_count: 2,
    working_hours: DAYS.map((d) => ({ day: d, open: "06:00", close: "22:00", closed: false })),
    membership_fee_min: 1500, membership_fee_max: 4000,
    photos: [], amenities: [],
    plan_tier: "starter",
  });

  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const totalSteps = user ? 3 : 4;
  const currentStepLabel = user ? step : step + 1;

  // Guest signup — inline. On success we auto-copy the account data into the
  // gym owner fields so the next step is pre-filled.
  const signupOwner = async () => {
    if (!f.account_email || !f.account_password) { toast.error("Email and password required"); return; }
    if (f.account_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (f.account_password !== f.account_password_confirm) { toast.error("Passwords do not match"); return; }
    if (!f.consent_age || !f.consent_terms) { toast.error("Please accept the age gate and Terms to continue"); return; }
    setBusy(true);
    try {
      await register(f.account_name, f.account_email, f.account_password, {
        age_confirmed: f.consent_age,
        terms_accepted: f.consent_terms,
        marketing_consent: false,
      });
      // Pre-fill downstream fields with the account info
      setF((p) => ({
        ...p,
        owner_name: p.account_name || p.owner_name,
        owner_email: p.account_email || p.owner_email,
        owner_phone: p.account_phone || p.owner_phone,
      }));
      setStep(1);
      toast.success("Account created — let's list your gym");
    } catch (e) {
      toast.error(e?.message || "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!user && !localStorage.getItem("ff_token")) {
      // Should never happen — Step 0 handles this. Belt-and-braces.
      toast("Please create your owner account first");
      setStep(0);
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/arena/gyms/apply", { ...f, referral_token: refToken });
      setSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) return <SubmittedScreen submitted={submitted} navigate={navigate} />;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-join">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => (step > initialStep ? setStep(step - 1) : navigate(-1))} aria-label="Back" style={{ background: "transparent", border: "none" }} data-testid="arena-back">
            <ChevronLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ArenaShield />
              <span style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>List your gym</span>
            </div>
            <div style={{ height: 4, background: "var(--bg-inset, #D8D6D1)", borderRadius: 100, marginTop: 8 }}>
              <div style={{ height: "100%", width: `${(currentStepLabel / totalSteps) * 100}%`, background: "#F5C518", borderRadius: 100, transition: "width 220ms ease" }} />
            </div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>Step {currentStepLabel} of {totalSteps}</div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pb-32 pt-4">
        {step === 0 && <Step0 f={f} update={update} onNext={signupOwner} busy={busy} navigate={navigate} />}
        {step === 1 && <Step1 f={f} update={update} onNext={() => setStep(2)} />}
        {step === 2 && <Step2 f={f} update={update} onNext={() => setStep(3)} />}
        {step === 3 && <Step3 f={f} update={update} onSubmit={submit} busy={busy} />}
      </main>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step 0 — Create Owner Account (only shown to guests). Zero-friction: password
// is set right here so the same email/password logs in at /login later.
// -----------------------------------------------------------------------------
function Step0({ f, update, onNext, busy, navigate }) {
  const [showPw, setShowPw] = useState(false);
  const ok =
    f.account_name.trim().length >= 2 &&
    /.+@.+\..+/.test(f.account_email) &&
    f.account_password.length >= 8 &&
    f.account_password === f.account_password_confirm &&
    f.consent_age &&
    f.consent_terms;

  const strength = (() => {
    const p = f.account_password;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s; // 0-4
  })();
  const strengthLabel = ["Too short", "Weak", "Fair", "Strong", "Excellent"][Math.min(4, strength)];

  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="arena-step-0">
      <div
        style={{
          background: "linear-gradient(135deg, rgba(245,197,24,0.15) 0%, rgba(245,197,24,0.06) 100%)",
          border: "1px solid rgba(245,197,24,0.35)",
          borderRadius: 14,
          padding: "12px 14px",
        }}
      >
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#B58A00" }}>
          Step 1 — Owner account
        </div>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 22, color: "#1A1A1A", marginTop: 4, lineHeight: 1.15 }}>
          Create your login now
        </div>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#4A4845", marginTop: 4, lineHeight: 1.5 }}>
          You&apos;ll use the same email + password to sign in at <b>fitfrik.com/login</b> once your gym is approved — we&apos;ll route you straight to your Arena Dashboard.
        </div>
      </div>

      <div>
        <FieldLabel>Your full name *</FieldLabel>
        <div style={{ position: "relative" }}>
          <UserIcon size={14} style={{ position: "absolute", left: 14, top: 15, color: "#8A8884" }} />
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            value={f.account_name}
            onChange={(e) => update("account_name", e.target.value)}
            data-testid="acct-name"
            autoComplete="name"
          />
        </div>
      </div>

      <div>
        <FieldLabel>Email *</FieldLabel>
        <div style={{ position: "relative" }}>
          <Mail size={14} style={{ position: "absolute", left: 14, top: 15, color: "#8A8884" }} />
          <input
            type="email"
            style={{ ...inputStyle, paddingLeft: 36 }}
            value={f.account_email}
            onChange={(e) => update("account_email", e.target.value)}
            data-testid="acct-email"
            autoComplete="email"
          />
        </div>
      </div>

      <div>
        <FieldLabel>Phone (WhatsApp)</FieldLabel>
        <input
          type="tel"
          style={inputStyle}
          value={f.account_phone}
          onChange={(e) => update("account_phone", e.target.value)}
          data-testid="acct-phone"
          autoComplete="tel"
          placeholder="+91"
        />
      </div>

      <div>
        <FieldLabel>Create password *</FieldLabel>
        <div style={{ position: "relative" }}>
          <Lock size={14} style={{ position: "absolute", left: 14, top: 15, color: "#8A8884" }} />
          <input
            type={showPw ? "text" : "password"}
            style={{ ...inputStyle, paddingLeft: 36, paddingRight: 40 }}
            value={f.account_password}
            onChange={(e) => update("account_password", e.target.value)}
            data-testid="acct-password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
          />
          <button
            type="button"
            onClick={() => setShowPw((s) => !s)}
            aria-label={showPw ? "Hide password" : "Show password"}
            style={{ position: "absolute", right: 8, top: 8, width: 28, height: 28, background: "transparent", border: 0, display: "grid", placeItems: "center" }}
          >
            {showPw ? <EyeOff size={14} color="#8A8884" /> : <Eye size={14} color="#8A8884" />}
          </button>
        </div>
        {f.account_password && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
            <div style={{ flex: 1, height: 4, background: "#E7E5E1", borderRadius: 100, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${(Math.max(1, strength) / 4) * 100}%`,
                  background: strength >= 3 ? "#0FB5A6" : strength === 2 ? "#F5C518" : "#E5484D",
                  transition: "width 180ms ease",
                }}
              />
            </div>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, color: "#8A8884", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {strengthLabel}
            </span>
          </div>
        )}
      </div>

      <div>
        <FieldLabel>Confirm password *</FieldLabel>
        <input
          type={showPw ? "text" : "password"}
          style={inputStyle}
          value={f.account_password_confirm}
          onChange={(e) => update("account_password_confirm", e.target.value)}
          data-testid="acct-password-confirm"
          autoComplete="new-password"
        />
      </div>

      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={f.consent_age}
          onChange={(e) => update("consent_age", e.target.checked)}
          data-testid="acct-consent-age"
          style={{ marginTop: 3 }}
        />
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#4A4845", lineHeight: 1.5 }}>
          I confirm I am 18 years or older and authorised to list this gym.
        </span>
      </label>

      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={f.consent_terms}
          onChange={(e) => update("consent_terms", e.target.checked)}
          data-testid="acct-consent-terms"
          style={{ marginTop: 3 }}
        />
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#4A4845", lineHeight: 1.5 }}>
          I accept the FitFrik <b>Terms</b> and <b>Privacy Policy</b>, including the Arena Pass gym partner agreement.
        </span>
      </label>

      <button
        onClick={onNext}
        disabled={!ok || busy}
        data-testid="step0-next"
        style={{
          height: 52,
          marginTop: 4,
          background: ok && !busy ? "#0C0C0B" : "var(--bg-inset, #D8D6D1)",
          color: ok && !busy ? "white" : "var(--text-tertiary, #8A8884)",
          border: "none",
          borderRadius: 100,
          fontFamily: "Barlow",
          fontSize: 14,
          fontWeight: 700,
          cursor: ok && !busy ? "pointer" : "not-allowed",
        }}
      >
        {busy ? "Creating account…" : "Create account & continue"}
      </button>

      <div style={{ textAlign: "center", marginTop: -4 }}>
        <button
          type="button"
          onClick={() => navigate("/login?next=/arena/join")}
          data-testid="acct-existing-login"
          style={{
            background: "transparent",
            border: 0,
            color: "#4A4845",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Already have a FitFrik account? <span style={{ color: "#00B0D5", fontWeight: 700 }}>Sign in</span>
        </button>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <label style={{ display: "block", fontFamily: "Barlow", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary, #8A8884)", marginBottom: 4 }}>{children}</label>;
}

const inputStyle = {
  width: "100%", height: 44, padding: "0 14px",
  background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))",
  borderRadius: 12, fontFamily: "Barlow", fontSize: 14, outline: "none", color: "var(--text-primary, #0C0C0B)",
};

function Step1({ f, update, onNext }) {
  const ok = f.gym_name && f.city && f.address && f.owner_name && f.owner_phone;
  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="arena-step-1">
      <div>
        <FieldLabel>Gym name *</FieldLabel>
        <input style={inputStyle} value={f.gym_name} onChange={(e) => update("gym_name", e.target.value)} data-testid="gym-name" />
      </div>
      <div>
        <FieldLabel>Gym type *</FieldLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {GYM_TYPES.map((t) => (
            <button key={t.v} onClick={() => update("gym_type", t.v)} data-testid={`gym-type-${t.v}`}
              style={{
                padding: "8px 14px", background: f.gym_type === t.v ? "#0C0C0B" : "transparent",
                color: f.gym_type === t.v ? "white" : "var(--text-secondary, #4A4845)",
                border: "1px solid", borderColor: f.gym_type === t.v ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))",
                borderRadius: 100, fontFamily: "Barlow", fontSize: 13, fontWeight: 700,
              }}>{t.l}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><FieldLabel>City *</FieldLabel><input style={inputStyle} value={f.city} onChange={(e) => update("city", e.target.value)} data-testid="gym-city" /></div>
        <div><FieldLabel>Pincode</FieldLabel><input style={inputStyle} value={f.pincode} onChange={(e) => update("pincode", e.target.value)} /></div>
      </div>
      <div><FieldLabel>Full address *</FieldLabel>
        <textarea value={f.address} onChange={(e) => update("address", e.target.value)} data-testid="gym-address"
          style={{ ...inputStyle, height: 80, padding: "10px 14px", resize: "vertical" }} />
      </div>
      <div><FieldLabel>Owner name *</FieldLabel><input style={inputStyle} value={f.owner_name} onChange={(e) => update("owner_name", e.target.value)} data-testid="gym-owner-name" /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><FieldLabel>Owner phone *</FieldLabel><input type="tel" style={inputStyle} value={f.owner_phone} onChange={(e) => update("owner_phone", e.target.value)} data-testid="gym-owner-phone" /></div>
        <div><FieldLabel>Owner email</FieldLabel><input type="email" style={inputStyle} value={f.owner_email} onChange={(e) => update("owner_email", e.target.value)} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><FieldLabel>Member count</FieldLabel><input type="number" style={inputStyle} value={f.member_count_approx} onChange={(e) => update("member_count_approx", parseInt(e.target.value, 10) || 0)} /></div>
        <div><FieldLabel>Trainer count</FieldLabel><input type="number" style={inputStyle} value={f.trainer_count} onChange={(e) => update("trainer_count", parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      <div>
        <FieldLabel>Working hours</FieldLabel>
        <div style={{ background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12, padding: 10 }}>
          {f.working_hours.map((wh, i) => (
            <div key={wh.day} style={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr 80px", gap: 6, alignItems: "center", padding: "5px 0" }}>
              <span style={{ fontFamily: "Barlow", fontSize: 13, fontWeight: 700 }}>{wh.day}</span>
              <input type="time" value={wh.open || ""} disabled={wh.closed}
                onChange={(e) => { const w = [...f.working_hours]; w[i] = { ...w[i], open: e.target.value }; update("working_hours", w); }}
                style={{ ...inputStyle, height: 36, fontSize: 12, opacity: wh.closed ? 0.4 : 1 }} />
              <input type="time" value={wh.close || ""} disabled={wh.closed}
                onChange={(e) => { const w = [...f.working_hours]; w[i] = { ...w[i], close: e.target.value }; update("working_hours", w); }}
                style={{ ...inputStyle, height: 36, fontSize: 12, opacity: wh.closed ? 0.4 : 1 }} />
              <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                <input type="checkbox" checked={wh.closed}
                  onChange={(e) => { const w = [...f.working_hours]; w[i] = { ...w[i], closed: e.target.checked }; update("working_hours", w); }} />
                Closed
              </label>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><FieldLabel>Min fee ₹</FieldLabel><input type="number" style={inputStyle} value={f.membership_fee_min} onChange={(e) => update("membership_fee_min", parseInt(e.target.value, 10) || 0)} /></div>
        <div><FieldLabel>Max fee ₹</FieldLabel><input type="number" style={inputStyle} value={f.membership_fee_max} onChange={(e) => update("membership_fee_max", parseInt(e.target.value, 10) || 0)} /></div>
      </div>

      <button onClick={onNext} disabled={!ok} data-testid="step1-next"
        style={{ height: 52, marginTop: 12, background: ok ? "#0C0C0B" : "var(--bg-inset, #D8D6D1)", color: ok ? "white" : "var(--text-tertiary, #8A8884)", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 700 }}>
        Next: Photos & amenities
      </button>
    </div>
  );
}

function Step2({ f, update, onNext }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const handleFile = async (file) => {
    if (!file) return;
    if (f.photos.length >= 5) { toast.error("Maximum 5 photos"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
    try {
      const compressed = await compressImage(file, { maxDim: 1600, targetKB: 800 });
      update("photos", [...f.photos, compressed]);
    } catch { toast.error("Could not load image"); }
  };
  const removePhoto = (i) => update("photos", f.photos.filter((_, idx) => idx !== i));
  const toggleAmenity = (a) => update("amenities", f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a]);

  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="arena-step-2">
      <FieldLabel>Photos (up to 5)</FieldLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))", gap: 8 }}>
        {f.photos.map((src, i) => (
          <div key={i} style={{ position: "relative", aspectRatio: "1/1" }}>
            <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} />
            <button onClick={() => removePhoto(i)} aria-label="Remove" data-testid={`photo-remove-${i}`}
              style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.65)", color: "white", border: "none", display: "grid", placeItems: "center" }}><X size={11} /></button>
          </div>
        ))}
        {f.photos.length < 5 && (
          <>
            <button onClick={() => cameraRef.current?.click()} data-testid="photo-camera"
              style={{ aspectRatio: "1/1", display: "grid", placeItems: "center", background: "var(--bg-surface, #E4E2DE)", border: "1px dashed var(--border, rgba(12,12,11,0.20))", borderRadius: 10 }}>
              <Camera size={20} />
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            <button onClick={() => galleryRef.current?.click()} data-testid="photo-gallery"
              style={{ aspectRatio: "1/1", display: "grid", placeItems: "center", background: "var(--bg-surface, #E4E2DE)", border: "1px dashed var(--border, rgba(12,12,11,0.20))", borderRadius: 10 }}>
              <ImageIcon size={20} />
            </button>
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </>
        )}
      </div>

      <FieldLabel>Amenities</FieldLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {AMENITIES.map((a) => (
          <button key={a} onClick={() => toggleAmenity(a)} data-testid={`amen-${a.replace(/\s+/g, "-")}`}
            style={{
              padding: "8px 12px", background: f.amenities.includes(a) ? "#0C0C0B" : "transparent",
              color: f.amenities.includes(a) ? "white" : "var(--text-secondary, #4A4845)",
              border: "1px solid", borderColor: f.amenities.includes(a) ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))",
              borderRadius: 100, fontFamily: "Barlow", fontSize: 12, fontWeight: 600,
            }}>{a}</button>
        ))}
      </div>

      <button onClick={onNext} data-testid="step2-next"
        style={{ height: 52, marginTop: 16, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 700 }}>
        Next: Choose your plan
      </button>
    </div>
  );
}

const PLANS = [
  { v: "free", l: "Free", price: 0, sub: "Up to 25 members", points: ["QR check-in", "Local Squad", "No analytics"] },
  { v: "starter", l: "Starter", price: 999, sub: "Up to 150 members", points: ["Check-in + analytics", "Trainer assignment", "At-risk alerts"], popular: true },
  { v: "pro", l: "Pro", price: 2499, sub: "Unlimited members", points: ["Full analytics + Arena Board TV", "Arena Challenges", "Form Intelligence"] },
];

function Step3({ f, update, onSubmit, busy }) {
  return (
    <div style={{ display: "grid", gap: 12 }} data-testid="arena-step-3">
      <FieldLabel>Choose a plan</FieldLabel>
      <div style={{ display: "grid", gap: 10 }}>
        {PLANS.map((p) => (
          <button key={p.v} onClick={() => update("plan_tier", p.v)} data-testid={`plan-${p.v}`}
            style={{
              textAlign: "left", padding: 16,
              background: f.plan_tier === p.v ? "#0C0C0B" : "var(--bg-surface, #E4E2DE)",
              color: f.plan_tier === p.v ? "white" : "var(--text-primary, #0C0C0B)",
              border: "1px solid", borderColor: f.plan_tier === p.v ? "#F5C518" : "var(--border, rgba(12,12,11,0.10))",
              borderRadius: 16, position: "relative",
            }}>
            {p.popular && <span style={{ position: "absolute", top: -10, right: 16, background: "#F5C518", color: "#0C0C0B", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 100, letterSpacing: "0.08em" }}>MOST POPULAR</span>}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>{p.l}</span>
              <span style={{ fontFamily: "Barlow Condensed", fontSize: 26, fontWeight: 900 }}>
                {p.price === 0 ? "Free" : `₹${p.price}`}
                {p.price > 0 && <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7 }}>/mo</span>}
              </span>
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{p.sub}</div>
            <ul style={{ margin: "10px 0 0 0", padding: 0, listStyle: "none", fontSize: 12 }}>
              {p.points.map((pt, i) => <li key={i} style={{ display: "flex", gap: 6, marginTop: 4 }}><Check size={12} style={{ marginTop: 2, color: "#F5C518" }} /> {pt}</li>)}
            </ul>
            {p.price > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: "#F5C518", marginTop: 10 }}>14-day free trial · cancel anytime</div>}
          </button>
        ))}
      </div>

      <button onClick={onSubmit} disabled={busy} data-testid="apply-submit"
        style={{ height: 52, marginTop: 16, background: "#F5C518", color: "#0C0C0B", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 800 }}>
        {busy ? "Submitting…" : "Submit application"}
      </button>
      <p style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", textAlign: "center" }}>
        We&apos;ll review within 24 hours and confirm via WhatsApp.
      </p>
    </div>
  );
}

function SubmittedScreen({ submitted, navigate }) {
  return (
    <div className="min-h-screen grid place-items-center px-6" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-submitted">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.45, ease: "easeOut" }}
        style={{ maxWidth: 420, textAlign: "center" }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <ArenaShield size={28} showLabel={false} />
        </div>
        <h1 style={{ fontFamily: "Barlow Condensed", fontSize: 28, fontWeight: 900 }}>Application received</h1>
        <p style={{ fontFamily: "Barlow", fontSize: 14, color: "var(--text-secondary, #4A4845)", marginTop: 8 }}>
          Our Arena team usually reviews within 24 hours. WhatsApp confirmation coming.
        </p>

        {/* Gym code */}
        <div style={{ marginTop: 18, padding: 16, background: "var(--bg-surface, #E4E2DE)", borderRadius: 12, border: "1px solid var(--border, rgba(12,12,11,0.10))", textAlign: "left" }}>
          <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Your gym code</div>
          <div style={{ fontFamily: "Barlow Condensed", fontSize: 36, fontWeight: 900, letterSpacing: "0.08em" }} data-testid="submitted-gym-code">{submitted.gym_code}</div>
          <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
            Members can search this code to find and link to your gym once you&apos;re approved.
          </p>
        </div>

        {/* What happens next — status ladder */}
        <div style={{ marginTop: 18, padding: 16, background: "#FFFFFF", borderRadius: 14, border: "1px solid #EEECE9", textAlign: "left" }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#8A8884", fontWeight: 700, marginBottom: 10 }}>
            What happens next
          </div>
          <StatusRow n="1" done label="Application submitted" sub="Right now — you&apos;re here" />
          <StatusRow n="2" active label="Under review by Arena team" sub="Usually within 24 hours" />
          <StatusRow n="3" label="Approval + in-app notification" sub="We&apos;ll ping your bell 🔔 and email you" />
          <StatusRow n="4" label="Sign in → Arena Dashboard" sub="Same email + password you just created" />
        </div>

        {/* Login CTA */}
        <button
          onClick={() => navigate("/login")}
          data-testid="submitted-goto-login"
          style={{
            width: "100%",
            height: 52,
            marginTop: 16,
            background: "#00D4FF",
            color: "#0C0C0B",
            border: "none",
            borderRadius: 100,
            fontFamily: "Barlow",
            fontSize: 14,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 6px 18px rgba(0,212,255,0.35)",
          }}
        >
          Go to Sign in
        </button>
        <button onClick={() => navigate("/home")} data-testid="submitted-home"
          style={{ width: "100%", height: 44, marginTop: 8, background: "transparent", color: "#4A4845", border: "1px solid #E7E5E1", borderRadius: 100, fontFamily: "Barlow", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Explore FitFrik meanwhile
        </button>
      </motion.div>
    </div>
  );
}

function StatusRow({ n, label, sub, done, active }) {
  const bg = done ? "#0FB5A6" : active ? "#F5C518" : "#E7E5E1";
  const fg = done || active ? "#0B0B0B" : "#8A8884";
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "flex-start" }}>
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: bg,
          color: fg,
          display: "grid",
          placeItems: "center",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: 11,
          flexShrink: 0,
        }}
      >
        {done ? <Check size={12} strokeWidth={3} /> : n}
      </div>
      <div>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A" }}>
          {label}
        </div>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#8A8884", marginTop: 1 }}>
          {sub}
        </div>
      </div>
    </div>
  );
}
